import { NewDeal, Product } from 'types'
import {
  calculateDealStatus,
  dedupeAndNormalizeProducts as centralDedupeAndNormalize,
  normalizeProduct,
  sanitizeProductForSync,
} from 'utils/dealCalculations'
import { parseInstanceFromSK } from 'utils/ids'

const draftStatuses = [
  'Draft',
  'Saved',
  'Created',
  'Requested',
  'Rejected',
  'Resolved',
  'Approved',
  'Approval Requested',
  'Updated',
  'Modified',
  'Needs Approval',
  'Completed Internally',
]

export const updateDealStatus = (deal: NewDeal, products: Product[]) => {
  if (!deal) return

  const newStatus = calculateDealStatus(deal.dealStatus, products)

  // Exit early if there's no actual change
  if (newStatus === deal.dealStatus) return

  return {
    PK: deal.PK,
    SK: deal.SK,
    fieldName: 'dealStatus',
    newValue: newStatus,
    tableName: 'ORGANIZATIONS_TABLE',
  }
}

export const normalizeDeal = (deal: NewDeal) => ({
  ...deal,
  recipients: {
    authorizedRecipient: deal?.recipients?.authorizedRecipient || [],
    contactRecipient: deal?.recipients?.contactRecipient || [],
  },
  organizations: deal?.organizations || [],
  contacts: deal?.contacts || [],
  owner: deal?.owner || [],
  createdBy: deal?.createdBy || [],
  primaryContact: deal?.primaryContact || [],
  isDraft: draftStatuses.includes(deal?.status),
  numberOfPayments:
    deal && ['CSA', 'RCSA']?.includes(deal?.type) && deal?.totals && deal?.totals?.total && deal?.totals?.total < 2501
      ? 1
      : deal?.numberOfPayments ?? 2,
})

/**
 * @deprecated Use normalizeProduct from 'utils/dealCalculations' directly
 */
export const normalizeProductRowOnLoad = (row: Product, dealStatus?: string, billingModel?: 'points' | 'legacy'): Product => {
  try {
    return normalizeProduct(row, 0, dealStatus, billingModel)
  } catch {
    // Fallback for malformed products - return as-is with basic identity fields
    const iid = row.instanceId ?? row.rowId ?? parseInstanceFromSK(row.SK)
    return {
      ...row,
      instanceId: iid,
      rowId: iid,
    } as Product
  }
}

export function normalizeProductOnClient(raw: any) {
  if (!raw?.PK || !raw?.SK) throw new Error('Product missing PK/SK')
  const iid = raw.instanceId ?? raw.rowId ?? parseInstanceFromSK(raw.SK)

  const sortOrder =
    typeof raw.sortOrder === 'number' ? raw.sortOrder : Number.isFinite(+raw.sortOrder) ? +raw.sortOrder : undefined

  return {
    ...raw,
    instanceId: iid, // keep for legacy paths
    rowId: iid,
    sortOrder,
  } as Product
}

/**
 * @deprecated Use sanitizeProductForSync from 'utils/dealCalculations' directly
 */
export const sanitizeForSync = (p: Product): Product => {
  return sanitizeProductForSync(p)
}

/**
 * @deprecated Use dedupeAndNormalizeProducts from 'utils/dealCalculations' directly
 */
export function dedupeAndNormalizeProducts(list: any[]): Product[] {
  return centralDedupeAndNormalize(list)
}

export const patchRowsForDeal = (current: Product[], syncResp?: any) => {
  if (!syncResp) return current

  const byKey = new Map(current.map(p => [`${p.PK}|${p.SK}`, p]))

  for (const d of syncResp.deletes ?? []) byKey.delete(`${d.PK}|${d.SK}`)
  for (const u of syncResp.upserts ?? []) byKey.set(`${u.PK}|${u.SK}`, u)

  return [...byKey.values()]
}
